// Catálogo local de proveedores/modelos del asistente IA (BYO).
//
// Es la COPIA de respaldo del catálogo que sirve el backend en
// GET helpdesk/ai-settings/catalog: si esa ruta aún no está desplegada
// (isEndpointMissing) o falla, la vista usa esta constante y todo sigue
// funcionando. Mantener en espejo con ai-settings.constants.ts del backend.
//
// IDs verificados contra la documentación oficial de cada proveedor
// (jul 2026). El backend valida `model` como string libre, así que un ID
// nuevo del proveedor siempre puede tipearse como "Modelo personalizado…".

// Mismo tope que AI_MODEL_MAX del backend (dto de update/test).
export const AI_MODEL_MAX = 100;

// Etiquetas de los badges por tier del modelo.
export const AI_BADGE_LABELS = {
  recomendado: "Recomendado",
  equilibrado: "Equilibrado",
  economico: "Económico",
  premium: "Premium",
};

export const AI_MODEL_CATALOG = [
  {
    id: "anthropic",
    label: "Anthropic (Claude)",
    keyUrl: "https://console.anthropic.com",
    models: [
      {
        id: "claude-haiku-4-5-20251001",
        label: "Claude Haiku 4.5",
        badge: "recomendado",
        pros: "Rápido y económico; tono natural, ideal para mensajería",
        cons: "Menos profundidad en casos muy complejos",
      },
      {
        id: "claude-sonnet-5",
        label: "Claude Sonnet 5",
        badge: "equilibrado",
        pros: "Excelente redacción y criterio para reclamos",
        cons: "Cuesta varias veces más que Haiku",
      },
      {
        id: "claude-opus-5",
        label: "Claude Opus 5",
        badge: "premium",
        pros: "Máxima calidad para casos delicados",
        cons: "Más caro y lento; innecesario para consultas simples",
      },
    ],
  },
  {
    id: "openai",
    label: "OpenAI (ChatGPT)",
    keyUrl: "https://platform.openai.com",
    models: [
      // gpt-5.1 / gpt-5.1-mini quedaron deprecados en la API de OpenAI;
      // estos son sus sucesores vigentes en el mismo tier.
      {
        id: "gpt-5.4-mini",
        label: "GPT-5.4 mini",
        badge: "recomendado",
        pros: "Muy buen chat, rápido y barato",
        cons: "Puede sonar genérico si el prompt no fija el tono",
      },
      {
        id: "gpt-5.6-terra",
        label: "GPT-5.6 Terra",
        badge: "equilibrado",
        pros: "Conversación muy natural, gran manejo de contexto",
        cons: "Costo medio",
      },
      {
        id: "gpt-4o-mini",
        label: "GPT-4o mini",
        badge: "economico",
        pros: "El más barato; suficiente para FAQs",
        cons: "Generación anterior; razonamiento limitado",
      },
    ],
  },
  {
    id: "gemini",
    label: "Google (Gemini)",
    keyUrl: "https://aistudio.google.com/apikey",
    models: [
      // "gemini-3-flash"/"gemini-3-pro" a secas no existen en la API:
      // el flash estable vigente es 3.6 y el pro vigente es 3.1 (preview).
      {
        id: "gemini-3.6-flash",
        label: "Gemini 3.6 Flash",
        badge: "recomendado",
        pros: "Muy rápido y económico; buen multilingüe",
        cons: "A veces demasiado escueto",
      },
      {
        id: "gemini-3.1-pro-preview",
        label: "Gemini 3.1 Pro",
        badge: "premium",
        pros: "Alta calidad de razonamiento",
        cons: "Mayor latencia y costo",
      },
      {
        id: "gemini-2.5-flash",
        label: "Gemini 2.5 Flash",
        badge: "economico",
        pros: "Estable y muy barato",
        cons: "Generación anterior",
      },
    ],
  },
  {
    id: "deepseek",
    label: "DeepSeek",
    // API OpenAI-compatible: https://api.deepseek.com/v1/chat/completions
    keyUrl: "https://platform.deepseek.com/api_keys",
    // Solo dos modelos vigentes: V4.1 Flash (el recomendado) y V4 Pro, que se
    // retira el 14-sep-2026 y desde entonces enruta a V4.1 Flash.
    models: [
      {
        id: "deepseek-flash",
        label: "DeepSeek V4.1 Flash",
        badge: "recomendado",
        pros: "Contexto de 1M tokens, soporta imágenes y es muy económico",
        cons: "Menos profundidad que los modelos premium en casos muy complejos",
      },
      {
        id: "deepseek-v4-pro",
        label: "DeepSeek V4 Pro",
        badge: "premium",
        pros: "Mayor razonamiento",
        cons: "Se retira el 14-sep-2026: sus llamadas se enrutan a V4.1 Flash",
      },
    ],
  },
  {
    id: "kimi",
    label: "Kimi (Moonshot AI)",
    // La plataforma redirige (301) de platform.moonshot.ai a platform.kimi.ai;
    // el endpoint de API sigue siendo api.moonshot.ai/v1/chat/completions.
    keyUrl: "https://platform.kimi.ai",
    models: [
      // kimi-k2-turbo-preview y kimi-k2-0711-preview llegaron a fin de vida
      // (mayo 2026); K2.6 y K3 son los modelos de chat vigentes.
      {
        id: "kimi-k2.6",
        label: "Kimi K2.6",
        badge: "recomendado",
        pros: "Económico y rápido; buen español",
        cons: "Plataforma más nueva, menos documentación",
      },
      {
        id: "kimi-k3",
        label: "Kimi K3",
        badge: "premium",
        pros: "El más capaz de Kimi; contexto muy largo",
        cons: "Más caro que K2.6",
      },
    ],
  },
  // Proveedor "trae tu propio endpoint": llega SIN modelos a propósito (el
  // usuario tipea el id del modelo que expone su gateway OpenAI-compatible).
  // normalizeAiCatalog debe conservarlo justo por eso — ver CUSTOM_PROVIDER_ID.
  {
    id: "custom",
    label: "Personalizado (OpenAI-compatible)",
    keyUrl: "",
    models: [],
  },
];

// Id del proveedor de endpoint propio. Se exporta para que las vistas lo
// comparen por constante y no por el literal "custom" repartido por el código.
export const CUSTOM_PROVIDER_ID = "custom";

/**
 * Valida y normaliza el catálogo que devuelve el backend. Devuelve null si el
 * contrato no calza (y el caller se queda con AI_MODEL_CATALOG): un catálogo
 * a medias es peor que la copia local completa.
 *
 * Excepción a "sin modelos no es proveedor": el proveedor `custom` vive sin
 * modelos por diseño (el usuario tipea el id a mano), así que se conserva —
 * descartarlo borraría la opción de endpoint propio del selector.
 * @param {unknown} raw respuesta del GET (p.ej. { ok, providers: [...] })
 * @returns {Array|null}
 */
export function normalizeAiCatalog(raw) {
  const list = Array.isArray(raw) ? raw : raw?.providers || raw?.catalog;
  if (!Array.isArray(list)) return null;
  const providers = list
    // `custom` se acepta también sin la clave `models`: es el proveedor que
    // vive sin modelos a propósito, así que un backend que omita el campo no
    // debe hacerlo desaparecer del selector.
    .filter(
      (p) =>
        p &&
        typeof p.id === "string" &&
        (Array.isArray(p.models) || p.id === CUSTOM_PROVIDER_ID),
    )
    .map((p) => ({
      id: p.id,
      label: typeof p.label === "string" ? p.label : p.id,
      keyUrl: typeof p.keyUrl === "string" ? p.keyUrl : "",
      models: (Array.isArray(p.models) ? p.models : [])
        .filter((m) => m && typeof m.id === "string")
        .map((m) => ({
          id: m.id,
          label: typeof m.label === "string" ? m.label : m.id,
          badge: AI_BADGE_LABELS[m.badge] ? m.badge : null,
          pros: typeof m.pros === "string" ? m.pros : "",
          cons: typeof m.cons === "string" ? m.cons : "",
        })),
    }))
    .filter((p) => p.models.length > 0 || p.id === CUSTOM_PROVIDER_ID);
  return providers.length > 0 ? providers : null;
}

/**
 * Modelo recomendado de un proveedor: el que lleva badge "recomendado" o, en
 * su defecto, el primero de la lista (espejo de AI_DEFAULT_MODELS del backend).
 * Devuelve "" cuando el proveedor no tiene modelos (p. ej. `custom`): ahí el
 * modelo lo escribe el usuario, no hay default que preseleccionar.
 * @param {{models: Array}|undefined} provider
 * @returns {string}
 */
export function recommendedModelId(provider) {
  if (!provider || !provider.models?.length) return "";
  const rec = provider.models.find((m) => m.badge === "recomendado");
  return (rec || provider.models[0]).id;
}
